import type IntlService from 'ember-intl/services/intl';
import type { LimitsStatusDataSignature } from '../services/tuition-assistance.ts';
import type { EligibilityStatus } from 'tio-data/data/resources/eligibility-status.type';
import { formatNumericDate } from './date/format-to-display.ts';
import { formatUsd } from './usd-format.ts';

// Blurb generation for the Approval Decision Banner
// Pure functions — no side effects, no component state.
// These produce human-readable paragraph summaries from the raw API data.

const BANNER = 'common.tas.approval.decision_banner';
const BLURB = `${BANNER}.blurb`;

// Shared label-key maps — exported for reuse by GUI components
const LOCK_BASIS_PROSE_KEYS: Record<string, string> = {
  COURSE_START_DATE: `${BLURB}.lock_basis.course_start_date`,
  COURSE_END_DATE: `${BLURB}.lock_basis.course_end_date`,
  FULFILLED_DATE: `${BLURB}.lock_basis.fulfilled_date`,
  ON_COURSES_SUBMISSION: `${BLURB}.lock_basis.on_courses_submission`,
  ON_SUBMISSION: `${BLURB}.lock_basis.on_submission`,
  APPROVED_DATE: `${BLURB}.lock_basis.approved_date`,
};

export const LOCK_BASIS_KEYS: Record<string, string> = {
  COURSE_START_DATE: `${BANNER}.lock_basis.course_start_date`,
  COURSE_END_DATE: `${BANNER}.lock_basis.course_end_date`,
  FULFILLED_DATE: `${BANNER}.lock_basis.fulfilled_date`,
  ON_COURSES_SUBMISSION: `${BANNER}.lock_basis.on_courses_submission`,
  ON_SUBMISSION: `${BANNER}.lock_basis.on_submission`,
  APPROVED_DATE: `${BANNER}.lock_basis.approved_date`,
};

export const PARTICIPANT_STATE_KEYS: Record<string, string> = {
  'TAS.ParticipantState.ACTIVE': `${BANNER}.participant_state.active`,
  'TAS.ParticipantState.ELIGIBLE': `${BANNER}.participant_state.eligible`,
  'TAS.ParticipantState.HELD': `${BANNER}.participant_state.held`,
  'TAS.ParticipantState.INACTIVE': `${BANNER}.participant_state.inactive`,
  'TAS.ParticipantState.TERMINATED': `${BANNER}.participant_state.terminated`,
};

export const LIMIT_LEVEL_KEYS: Record<string, string> = {
  company_annual: `${BANNER}.limit_level.company_annual`,
  company: `${BANNER}.limit_level.company`,
  product_annual: `${BANNER}.limit_level.product_annual`,
  product: `${BANNER}.limit_level.product`,
  batch_annual: `${BANNER}.limit_level.batch_annual`,
  batch: `${BANNER}.limit_level.batch`,
  scheme_annual: `${BANNER}.limit_level.scheme_annual`,
  scheme: `${BANNER}.limit_level.scheme`,
  none: `${BANNER}.limit_level.none`,
};

// Lowercase variants for prose blurbs
const LIMIT_LEVEL_PROSE_KEYS: Record<string, string> = {
  company_annual: `${BLURB}.limit_level.company_annual`,
  company: `${BLURB}.limit_level.company`,
  product_annual: `${BLURB}.limit_level.product_annual`,
  product: `${BLURB}.limit_level.product`,
  batch_annual: `${BLURB}.limit_level.batch_annual`,
  batch: `${BLURB}.limit_level.batch`,
  scheme_annual: `${BLURB}.limit_level.scheme_annual`,
  scheme: `${BLURB}.limit_level.scheme`,
  none: `${BLURB}.limit_level.none`,
};

export const EMPLOYMENT_TYPE_KEYS: Record<string, string> = {
  FULL_TIME: `${BANNER}.employment_type.full_time`,
  PART_TIME: `${BANNER}.employment_type.part_time`,
  CONTRACT: `${BANNER}.employment_type.contract`,
  TEMPORARY: `${BANNER}.employment_type.temporary`,
  INTERN: `${BANNER}.employment_type.intern`,
  SEASONAL: `${BANNER}.employment_type.seasonal`,
};

/**
 * Translates `value` through `keys`, falling back when the map has no entry.
 *
 * @param fallback returned for an unmapped value; defaults to the value itself
 * @returns the translated label or the fallback
 */
export function labelFor(
  intl: IntlService,
  keys: Record<string, string>,
  value: string,
  fallback: string = value
): string {
  const key = keys[value];
  return key ? intl.t(key) : fallback;
}

/**
 * Formats an API date as a short numeric date in the active locale: "5/4/2026" in
 * en-US, "4/5/2026" in French. Date-only values are read as local calendar dates so
 * they never shift a day.
 *
 * @returns the formatted date, or '' when there is no value
 */
export function formatBannerDate(intl: IntlService, value?: string | null): string {
  if (!value) return '';
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(value);
  return formatNumericDate(date, intl, { dayMonth: 'numeric' });
}

/**
 * Formats a cents amount as USD in the active locale ("$5,250.00" / "$5 250,00").
 *
 * @param cents amount in cents
 */
export function formatBannerDollars(intl: IntlService, cents: number): string {
  return formatUsd(cents / 100, intl.primaryLocale ?? 'en-us', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Formats a credit count with its unit ("1 credit", "3 crédits").
 *
 * @param count number of credits
 */
export function formatBannerCredits(intl: IntlService, count: number): string {
  return intl.t(`${BANNER}.credits`, { count, n: String(count) });
}

/**
 * Generates a human-readable paragraph explaining the eligibility determination.
 *
 * Follows the template logic from the "Eligibility LLM Instruction" document:
 * - Scenario A: Admin override (`ignorePolicies` is true)
 * - Scenario B: Standard eligibility logic with lock basis, participant state, employment
 *
 * @param intl translates the paragraph and formats its dates
 * @returns the paragraph in the active locale
 */
export function generateEligibilityBlurb(data: EligibilityStatus, intl: IntlService): string {
  // Scenario A: Admin override
  if (data.ignorePolicies) {
    return intl.t(`${BLURB}.admin_override`);
  }

  const parts: string[] = [];
  const date = (value?: string | null) => formatBannerDate(intl, value);

  // Lock basis context
  const basisLabel = labelFor(
    intl,
    LOCK_BASIS_PROSE_KEYS,
    data.lockBasis,
    data.lockBasis?.replace(/_/g, ' ').toLowerCase()
  );
  const stateLabel = labelFor(
    intl,
    PARTICIPANT_STATE_KEYS,
    data.participantState ?? '',
    intl.t(`${BANNER}.participant_state.unknown`)
  );

  if (data.lockDate) {
    parts.push(
      intl.t(`${BLURB}.locked`, {
        basis: basisLabel,
        date: date(data.lockDate),
        state: stateLabel,
      })
    );
  } else {
    parts.push(intl.t(`${BLURB}.lock_pending`, { basis: basisLabel, state: stateLabel }));
  }

  // Employment context
  const empType = data.employmentType;
  if (empType) {
    const empLabel = labelFor(
      intl,
      EMPLOYMENT_TYPE_KEYS,
      empType,
      empType.replace(/_/g, ' ')
    ).toLocaleLowerCase(intl.primaryLocale);
    parts.push(intl.t(`${BLURB}.employment_type`, { type: empLabel }));
  }

  if (data.deactivatedOn) {
    const key = data.payDeactivatedEmployees ? 'deactivated_paid' : 'deactivated_unpaid';
    parts.push(intl.t(`${BLURB}.${key}`, { date: date(data.deactivatedOn) }));
  }

  // Eligibility-period context — the locked date must fall within the
  // employee's eligibility period(s) for the program.
  if (data.withinEligibilityPeriod === false) {
    const periods = data.eligibilityPeriods ?? [];
    const rangeText = periods.map(([start, end]) => `${date(start)} – ${date(end)}`).join('; ');
    const lockedDate = data.lockDate ? ` (${date(data.lockDate)})` : '';
    parts.push(
      rangeText
        ? intl.t(`${BLURB}.outside_period_with_ranges`, {
            lockedDate,
            count: periods.length,
            ranges: rangeText,
          })
        : intl.t(`${BLURB}.outside_period`, { lockedDate })
    );
  }

  // Final determination
  if (data.isEligible) {
    parts.push(intl.t(`${BLURB}.eligible`));
  } else {
    const reasons: string[] = [];
    const state = data.participantState;
    if (state !== 'TAS.ParticipantState.ACTIVE' && state !== 'TAS.ParticipantState.ELIGIBLE') {
      reasons.push(intl.t(`${BLURB}.reason.participant_state`, { state: stateLabel }));
    }
    if (data.deactivatedOn && !data.payDeactivatedEmployees) {
      reasons.push(intl.t(`${BLURB}.reason.deactivated`));
    }
    if (data.withinEligibilityPeriod === false) {
      reasons.push(intl.t(`${BLURB}.reason.outside_period`));
    }
    parts.push(
      reasons.length > 0
        ? intl.t(`${BLURB}.not_eligible_because`, {
            reasons: reasons.join(intl.t(`${BLURB}.reason.joiner`)),
          })
        : intl.t(`${BLURB}.not_eligible`)
    );
  }

  return parts.join(' ');
}

/**
 * Generates a plain-language summary of the limits/benefit situation.
 *
 * Based on the "Standard Response Template (Human Readable)" from the
 * Limits API v2 documentation.
 *
 * @param intl translates the summary and formats its dates and amounts
 * @returns the summary in the active locale
 */
export function generateLimitsSummary(data: LimitsStatusDataSignature, intl: IntlService): string {
  const parts: string[] = [];
  const isCredits = data.limit_capacity === 'TAS.LimitCapacity.CREDITS';

  const formatAmount = (cents: number) =>
    isCredits ? formatBannerCredits(intl, cents) : formatBannerDollars(intl, cents);
  const proseLevel = (level: string) => labelFor(intl, LIMIT_LEVEL_PROSE_KEYS, level);

  // Date context
  const asOf = {
    asOf: data.as_of_date ? 'yes' : 'no',
    date: formatBannerDate(intl, data.as_of_date),
  };

  // Available amount + constraining level (follows documented template)
  if (data.available !== null && data.available !== undefined) {
    if (data.soft_limit !== 'none') {
      const softLabel = proseLevel(data.soft_limit);
      parts.push(
        intl.t(`${BLURB}.limits.available_constrained`, {
          ...asOf,
          amount: formatAmount(data.available),
          level: softLabel,
        })
      );

      // Extract the constraining scope's limit and usage
      const scopeKey = data.soft_limit.replace(/_annual$/, '') as keyof typeof data.details;
      const scopeDetail = data.details?.[scopeKey];
      const scopeLimit = scopeDetail?.annual_limit;
      if (scopeDetail && scopeLimit != null) {
        const scopeUsage = scopeDetail.annual_usage;
        parts.push(
          scopeUsage === 0
            ? intl.t(`${BLURB}.limits.scope_all_available`, {
                level: softLabel,
                limit: formatAmount(scopeLimit),
                credits: isCredits ? 'yes' : 'no',
              })
            : intl.t(`${BLURB}.limits.scope_used`, {
                level: softLabel,
                limit: formatAmount(scopeLimit),
                used: formatAmount(scopeUsage),
              })
        );
      }
    } else {
      parts.push(
        intl.t(`${BLURB}.limits.remaining`, { ...asOf, amount: formatAmount(data.available) })
      );
    }
  } else {
    parts.push(intl.t(`${BLURB}.limits.unlimited`, asOf));
  }

  // Limit context
  if (data.hard_limit !== 'none') {
    if (data.soft_limit === data.hard_limit) {
      parts.push(intl.t(`${BLURB}.limits.hard_same`));
    } else {
      parts.push(intl.t(`${BLURB}.limits.hard_cap`, { level: proseLevel(data.hard_limit) }));
    }
  } else if (data.soft_limit !== 'none') {
    parts.push(intl.t(`${BLURB}.limits.soft`));
  }

  if (data.annual_start_date && data.annual_end_date) {
    parts.push(
      intl.t(`${BLURB}.limits.benefit_year`, {
        start: formatBannerDate(intl, data.annual_start_date),
        end: formatBannerDate(intl, data.annual_end_date),
      })
    );
  }

  return parts.join(' ');
}
